import axios from 'axios';

// Clerk token getter — set by ClerkSync in App.jsx when Clerk is configured
let _clerkGetToken = null;

/**
 * Wire in Clerk's getToken function so the request interceptor can inject
 * a fresh Clerk JWT. Pass null to stop sending Authorization headers.
 */
export const setClerkTokenGetter = (getter) => {
  _clerkGetToken = getter;
};

// Create a simple event emitter for auth failures
let authFailureListeners = [];
/**
 * Registers a callback to be invoked on authentication failure (401/403).
 * Used by App.jsx to trigger logout flow.
 *
 * @param {Function} callback - Function to run on auth failure
 * @returns {Function} Unsubscribe function
 */
export const onAuthFailure = (callback) => {
  authFailureListeners.push(callback);
  return () => {
    authFailureListeners = authFailureListeners.filter(cb => cb !== callback);
  };
};

const notifyAuthFailure = () => {
  authFailureListeners.forEach((callback) => {
    // Isolate each listener: a throwing listener must NOT propagate out of the
    // interceptor and replace the original API error before Promise.reject.
    try {
      callback();
    } catch (e) {
      if (import.meta.env?.DEV) console.error('onAuthFailure listener threw:', e);
    }
  });
};

// Limit-exceeded (paywall) listeners — fired when the API returns 403 with
// error_code "LIMIT_EXCEEDED". The PaywallProvider (App.jsx) registers a
// listener to surface the upgrade modal anywhere a write is blocked. The
// original request still rejects (see the interceptor) so each caller's catch
// runs — the modal is a side-effect, not a swallow.
let limitExceededListeners = [];
/**
 * Registers a callback invoked when a write is blocked by a resource limit
 * (HTTP 403, error_code "LIMIT_EXCEEDED"). The callback receives the response
 * `details` object ({ category, current_usage, limit, message }).
 *
 * @param {Function} callback - Function to run with the limit details
 * @returns {Function} Unsubscribe function
 */
export const onLimitExceeded = (callback) => {
  limitExceededListeners.push(callback);
  return () => {
    limitExceededListeners = limitExceededListeners.filter(cb => cb !== callback);
  };
};

const notifyLimitExceeded = (details) => {
  limitExceededListeners.forEach((callback) => {
    // Isolate each listener (see notifyAuthFailure) — a buggy paywall listener
    // must not mask the real request error.
    try {
      callback(details);
    } catch (e) {
      if (import.meta.env?.DEV) console.error('onLimitExceeded listener threw:', e);
    }
  });
};

// Rate-limited listeners — fired when the API returns 429 with error_code
// "RATE_LIMIT_EXCEEDED" (utils/rate_limiting.py). App.jsx registers a
// listener that toasts the server's "try again in N minute(s)" message;
// previously a 429 surfaced as each caller's generic failure copy.
let rateLimitedListeners = [];
/**
 * Registers a callback invoked when a request is rejected by the rate
 * limiter (HTTP 429). The callback receives the server's message string.
 *
 * @param {Function} callback - Function to run with the message
 * @returns {Function} Unsubscribe function
 */
export const onRateLimited = (callback) => {
  rateLimitedListeners.push(callback);
  return () => {
    rateLimitedListeners = rateLimitedListeners.filter(cb => cb !== callback);
  };
};

const notifyRateLimited = (message) => {
  rateLimitedListeners.forEach((callback) => {
    // Isolate each listener (see notifyAuthFailure).
    try {
      callback(message);
    } catch (e) {
      if (import.meta.env?.DEV) console.error('onRateLimited listener threw:', e);
    }
  });
};

// Create an Axios instance for API calls
const axiosInstance = axios.create({
  baseURL: '/api/v1', // Default base URL, can be overridden
});

const HttpService = {
  /**
   * Performs a GET request.
   * @param {string} url - The endpoint URL
   * @param {Object} [config] - Axios request config
   * @returns {Promise} Axios response promise
   */
  get: (url, config) => axiosInstance.get(url, config),

  /**
   * Performs a POST request.
   * @param {string} url - The endpoint URL
   * @param {Object} data - Request body data
   * @param {Object} [config] - Axios request config
   * @returns {Promise} Axios response promise
   */
  post: (url, data, config) => axiosInstance.post(url, data, config),

  /**
   * Performs a PUT request.
   * @param {string} url - The endpoint URL
   * @param {Object} [data] - Request body data (omit for body-less PUTs, e.g.
   *   "set primary image" endpoints that derive everything from the URL)
   * @param {Object} [config] - Axios request config
   * @returns {Promise} Axios response promise
   */
  put: (url, data, config) => axiosInstance.put(url, data, config),

  /**
   * Performs a PATCH request.
   * @param {string} url - The endpoint URL
   * @param {Object} data - Request body data
   * @param {Object} [config] - Axios request config
   * @returns {Promise} Axios response promise
   */
  patch: (url, data, config) => axiosInstance.patch(url, data, config),

  /**
   * Performs a DELETE request.
   * @param {string} url - The endpoint URL
   * @param {Object} [config] - Axios request config
   * @returns {Promise} Axios response promise
   */
  delete: (url, config) => axiosInstance.delete(url, config),
};

// On a cold load right after sign-in, Clerk's in-memory session can lag the
// session cookies, so getToken() briefly returns null. Firing the request then
// would send NO Authorization header → a spurious 401 (and, in the SPA, a shell
// that never renders). Retry a small, bounded number of times before giving up
// so a transient null doesn't surface as an auth failure. Exported (underscore-
// prefixed) so the retry contract can be unit-tested without an axios mock.
export const TOKEN_RETRY_ATTEMPTS = 3;
export const TOKEN_RETRY_DELAY_MS = 150;

const _sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const _getClerkTokenWithRetry = async () => {
  if (!_clerkGetToken) return null;
  for (let attempt = 0; attempt < TOKEN_RETRY_ATTEMPTS; attempt++) {
    try {
      const token = await _clerkGetToken();
      if (token) return token;
    } catch (e) {
      // getToken can throw while the session is mid-initialization; treat it
      // like a null result and retry rather than letting it abort the request.
      if (import.meta.env?.DEV) console.warn('Clerk getToken threw; retrying:', e);
    }
    // No delay after the final attempt — nothing follows it.
    if (attempt < TOKEN_RETRY_ATTEMPTS - 1) await _sleep(TOKEN_RETRY_DELAY_MS);
  }
  return null;
};

// Request interceptor — async so we can await Clerk's getToken()
axiosInstance.interceptors.request.use(
  async (config) => {
    const token = await _getClerkTokenWithRetry();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Attach CSRF token if available
    // We assume the token is set in axios.defaults by the app initializer
    if (axios.defaults.headers.common['X-CSRFToken']) {
      config.headers['X-CSRFToken'] = axios.defaults.headers.common['X-CSRFToken'];
    } else {
      // Fallback: try to read from cookie
      const match = document.cookie.match(/csrftoken=([^;]+)/);
      if (match) {
        config.headers['X-CSRFToken'] = match[1];
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response-error handler. Exported (underscore-prefixed) so the routing contract
// can be unit-tested without an axios mock adapter. Routes 401 → auth-failure,
// 403 LIMIT_EXCEEDED → paywall, and ALWAYS rejects so every caller's catch runs
// (the notifications are side-effects, not a swallow).
export const _handleResponseError = (error) => {
  const originalRequest = error.config || {};
  if (error.response?.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;
    notifyAuthFailure();
  } else if (
    error.response?.status === 403 &&
    error.response?.data?.error_code === 'LIMIT_EXCEEDED'
  ) {
    // Surface the upgrade paywall. Still reject below so the caller's catch
    // runs (e.g. the create form can stop its spinner / not show a generic toast).
    notifyLimitExceeded(error.response.data.details || {});
  } else if (
    error.response?.status === 429 &&
    error.response?.data?.error_code === 'RATE_LIMIT_EXCEEDED'
  ) {
    // The rate-limiter body carries the message under `error` (not
    // `message`). Toasted globally via the App.jsx listener; still rejects.
    notifyRateLimited(error.response.data.error || 'Rate limit exceeded. Please try again shortly.');
  }
  return Promise.reject(error);
};

// Register the handler on the response interceptor.
axiosInstance.interceptors.response.use((response) => response, _handleResponseError);

export default HttpService;
